import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Close, KeyboardArrowDown } from '@mui/icons-material';

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  z-index: 1000;
  background: ${props => props.isScrolled ? 'rgba(253, 251, 247, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.isScrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${props => props.isScrolled ? '0 4px 20px rgba(0,0,0,0.05)' : 'none'};
  transition: all 0.3s ease-in-out;
  color: ${props => props.isScrolled || props.isDarkText ? 'var(--color-text)' : 'white'};
`;

const NavInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
`;

const Logo = styled.div`
  font-family: var(--font-serif);
  font-weight: 700;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
  color: inherit;
  
  img {
    height: 50px;
    width: auto;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  font-family: var(--font-sans);
  font-weight: 500;
  font-size: 0.95rem;
  color: inherit;
  text-decoration: none;
  position: relative;
  opacity: 0.9;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 1px;
    background-color: currentColor;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-sans);
  font-weight: 500;
  font-size: 0.95rem;
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 16px;
  background: white;
  border-radius: 8px;
  padding: 8px;
  min-width: 220px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.05);

  a {
    display: block;
    padding: 12px 16px;
    color: var(--color-text);
    text-decoration: none;
    font-size: 0.9rem;
    transition: background 0.2s;
    border-radius: 4px;

    &:hover {
      background: rgba(0,0,0,0.03);
      color: var(--color-primary-blue);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-bg);
  z-index: 999;
  padding: 100px 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  a {
    font-family: var(--font-serif);
    font-size: 2rem;
    color: var(--color-text);
    text-decoration: none;
    font-weight: 600;
  }
`;

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Dark text on non-home pages or when scrolled
  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const isDarkText = !isHomePage || isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const aboutLinks = [
    { to: '/', text: 'Home' },
    { to: '/become-a-member', text: 'Become a Member' },
    { to: '/statuts', text: 'Association Chart' },
    { to: '/comitee-members', text: 'Committee Members' },
  ];

  return (
    <NavbarContainer isScrolled={isScrolled} isDarkText={isDarkText}>
      <NavInner>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Logo>
            {/* If logo image exists, usually it's better to use text for this style unless logo is very minimal
                 I will try to use text 'SHALOM' if the logo is not strictly required, or the logo img with filter.
                 For now, let's keep the logo img but fallback to text if needed. 
                 Actually, let's just use Text for a cleaner look as requested "do over", unless User really wants the logo. 
                 I'll add the img but with a max-height. */}
            <img src={process.env.PUBLIC_URL + "/logos/shalom.png"} alt="SHALOM" style={{ maxHeight: '50px' }} />
          </Logo>
        </Link>

        <NavLinks>
          <NavLink to="/activities">Events</NavLink>

          <DropdownContainer
            onMouseEnter={() => setShowDropdown(true)}
            onMouseLeave={() => setShowDropdown(false)}
          >
            About <KeyboardArrowDown fontSize="small" />
            <AnimatePresence>
              {showDropdown && (
                <DropdownMenu
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  {aboutLinks.map((link) => (
                    <Link key={link.to} to={link.to}>{link.text}</Link>
                  ))}
                </DropdownMenu>
              )}
            </AnimatePresence>
          </DropdownContainer>

          <NavLink to="/contact">Contact</NavLink>

          <Link to="/donation" style={{ textDecoration: 'none' }}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                background: isDarkText ? 'var(--color-primary-blue)' : 'white',
                color: isDarkText ? 'white' : 'var(--color-primary-blue)',
                padding: '10px 24px',
                borderRadius: '50px',
                fontWeight: 600,
                fontSize: '0.9rem',
                fontFamily: 'var(--font-sans)'
              }}
            >
              Support Us
            </motion.div>
          </Link>
        </NavLinks>

        <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)}>
          <Menu />
        </MobileMenuButton>
      </NavInner>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div style={{ position: 'absolute', top: 24, right: 24 }}>
              <button onClick={() => setIsMobileMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <Close fontSize="large" />
              </button>
            </div>

            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
            <Link to="/activities" onClick={() => setIsMobileMenuOpen(false)}>Events</Link>
            <Link to="/become-a-member" onClick={() => setIsMobileMenuOpen(false)}>Membership</Link>
            <Link to="/comitee-members" onClick={() => setIsMobileMenuOpen(false)}>Committee</Link>
            <Link to="/statuts" onClick={() => setIsMobileMenuOpen(false)}>Charte</Link>
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</Link>
            <Link to="/donation" onClick={() => setIsMobileMenuOpen(false)}>Support Us</Link>
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavbarContainer>
  );
};

export default Navbar;